// The job sheet's photos (Amir, 24 Oct: "why don't I see photos in the jobs?"): a job's photos, videos
// and documents from attachments.list, as Review's gallery and a customer's Photos tab show them. The
// signature is the invoice's, not a photo; a message's wrapper or a contact card sits under "Other files".

import { photoFileName, photoNote } from '@flowology/domain';
import type { PhotoStripProps } from '@flowology/ui-web';
import type { Attachment } from '../review/review-model.ts';
import { galleryOf, takers, videoUploadWords, type GalleryFilter } from '../shared/gallery-model.ts';
import { CATEGORY_WORD, isOtherFile, namesOf, tileOf } from '../shared/photo-display-model.ts';
import { holds } from './job-model.ts';

/** How many show before "Show all": a job with hundreds of photos draws two rows at first. */
export const FIRST_PHOTOS = 8;

/** The job's files in the strip, in attachments.list's order (oldest first), and its other files. */
export function jobFiles(all: readonly Attachment[]): { strip: Attachment[]; others: Attachment[] } {
  const kept = all.filter((a) => a.category !== 'signature');
  return { strip: kept.filter((a) => !isOtherFile(a)), others: kept.filter(isOtherFile) };
}

/** What the viewer pages through: the pictures and the videos that can play, in the strip's order. */
export const viewableOf = (strip: readonly Attachment[]) =>
  strip.filter((a) => {
    const t = tileOf(a).t;
    return t === 'picture' || t === 'video';
  });

/**
 * Who changes a photo here: whoever reviews jobs (attachments.setVisibility is jobs.complete's, the
 * note media.add's, both theirs). While the company mirrors ServiceTitan its photos' visibility and
 * notes are ServiceTitan's (QA-650), so the sheet only shows them.
 */
export const photoRights = (held: readonly string[], mirroring: boolean) => {
  const may = holds(held, ['jobs.complete']) && !mirroring;
  return { share: may, caption: may };
};

/** Who took it and when, in words: "Morgan T., Oct 14, 2026, 10:06 a.m.". */
export function takenText(a: Pick<Attachment, 'addedBy' | 'capturedAt'>, timeZone: string): string | null {
  const when = a.capturedAt
    ? new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(a.capturedAt),
      )
    : null;
  return [a.addedBy, when].filter(Boolean).join(', ') || null;
}

/**
 * The gallery's tools (GalleryTools) over the job's strip: whether any are videos, who took them, each
 * with how many of the kind chosen, and how many the choice shows.
 */
export function galleryToolsOf(strip: readonly Attachment[], f: GalleryFilter) {
  const ofKind = strip.filter((a) => f.kind === 'all' || a.kind === f.kind);
  return {
    hasVideos: strip.some((a) => a.kind === 'video'),
    people: takers(strip).map((name) => ({
      name,
      count: ofKind.filter((a) => a.addedBy === name).length,
    })),
    shown: galleryOf(strip, f).length,
    total: strip.length,
  };
}

/** Blueprint's strip (ui-web's PhotoStrip), from the job's files, as the gallery's tools choose. */
export function stripProps(
  all: readonly Attachment[],
  o: { jobNumber: string; timeZone: string; now: Date },
  /** Without it, every one in attachments.list's order. */
  f?: GalleryFilter,
): PhotoStripProps {
  const { strip, others } = jobFiles(all);
  // a note, else a made-up name numbered in its category (P-0161): never the file's own name
  const names = namesOf([...strip, ...others]);
  return {
    jobNumber: o.jobNumber,
    first: FIRST_PHOTOS,
    photos: (f ? galleryOf(strip, f) : strip).map((a) => {
      const tile = tileOf(a);
      const tag = CATEGORY_WORD[a.category] ?? a.category;
      return {
        id: a.id,
        tag,
        caption: photoNote(a.caption),
        name: names.get(a.id) ?? tag,
        by: takenText(a, o.timeZone),
        shared: a.visibility === 'customer',
        canShare: a.kind === 'photo',
        // a video still on its way up says how far it got (BL-082)
        tile:
          tile.t === 'waiting' && a.kind === 'video'
            ? { t: 'waiting', words: videoUploadWords(a, o.now) ?? tile.words }
            : tile,
      };
    }),
    files: others.map((a) => ({
      id: a.id,
      name: names.get(a.id) ?? 'A file',
      type: a.contentType,
      href: a.url,
      file: photoFileName(a.caption),
    })),
  };
}
